import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { formatDate } from '../../utils/dateFormat';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NONE = '_none';

const saveFields = async (record, body, onSaved) => {
  try {
    const { data } = await axios.put(`${API}/referral-tracker/records/${record.id}/fields`, body);
    toast.success('Referral updated');
    onSaved(data);
    return true;
  } catch (e) {
    toast.error(e.response?.data?.detail || 'Could not update referral');
    return false;
  }
};

export const AbawdCell = ({ record, options, onSaved }) => (
  <Select value={record.abawd_status || NONE} onValueChange={(v) => saveFields(record, { abawd_status: v === NONE ? null : v }, onSaved)}>
    <SelectTrigger className={`h-8 w-32 text-xs ${record.abawd_status ? '' : 'border-amber-400 bg-amber-50'}`} data-testid={`rt-abawd-${record.id}`}>
      <SelectValue placeholder="Set status" />
    </SelectTrigger>
    <SelectContent>
      <SelectItem value={NONE}>Not set</SelectItem>
      {options.abawd.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
    </SelectContent>
  </Select>
);

const isIso = (v) => !!v && /^\d{4}-\d{2}-\d{2}$/.test(v);

export const endsThisMonth = (record) => {
  if (!isIso(record.certification_end)) return false;
  const now = new Date();
  return record.certification_end.slice(0, 7) === `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

export const CertPeriodCell = ({ record, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const has = isIso(record.certification_start) || isIso(record.certification_end);
  const endingSoon = endsThisMonth(record);
  const tone = !has ? 'border border-amber-400 bg-amber-50 text-amber-800' : endingSoon ? 'border border-red-400 bg-red-50 text-red-800 font-medium' : '';
  const onOpen = (o) => { setOpen(o); if (o) { setStart(record.certification_start || ''); setEnd(record.certification_end || ''); } };
  const save = async () => {
    if (await saveFields(record, { certification_start: start || null, certification_end: end || null }, onSaved)) setOpen(false);
  };
  return (
    <Popover open={open} onOpenChange={onOpen}>
      <PopoverTrigger asChild>
        <button type="button" className={`text-left text-xs rounded-md px-2 py-1.5 whitespace-nowrap hover:bg-muted/70 ${tone}`} data-testid={`rt-cert-${record.id}`} data-ends-this-month={endingSoon ? 'true' : 'false'}>
          {has ? <>{formatDate(record.certification_start) || '—'} – {formatDate(record.certification_end) || '—'}</> : 'Set period'}
          {endingSoon && <span className="block text-[10px] uppercase tracking-wide" data-testid={`rt-cert-ending-${record.id}`}>Ends this month</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 space-y-3" data-testid={`rt-cert-popover-${record.id}`}>
        <p className="text-sm font-semibold">Certification Period</p>
        <div className="space-y-1.5"><Label>Start Date</Label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="rt-cert-start" /></div>
        <div className="space-y-1.5"><Label>End Date</Label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="rt-cert-end" /></div>
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="outline" onClick={() => setOpen(false)} data-testid="rt-cert-cancel">Cancel</Button>
          <Button size="sm" onClick={save} data-testid="rt-cert-save">Save</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
